import { useEffect, useState, useCallback } from 'react';
import { apiGet, ApiClientError } from '@client/lib/api';
import { todayIn, startOfMonth, addMonths, addDays } from '@shared/time';
import { formatCents } from '@shared/money';
import styles from './AnalyticsPage.module.css';

/** The org shape returned by GET /api/v1/auth/session. */
interface OrgSession {
  organization: { id: string; name: string; timezone: string } | null;
}

/** Response from GET /api/v1/reports/revenue. */
interface RevenueReport {
  /** Tax-EXCLUSIVE: what the business earned. */
  netRevenueCents: number;
  /** Gross minus net: money held for the state, never income. */
  taxCents: number;
  totalInvoicedCents: number;
  totalCollectedCents: number;
  outstandingCents: number;
}

/** Response from GET /api/v1/reports/jobs. */
interface JobsReport {
  completedCount: number;
  totalRevenueCents: number;
}

/** Response from GET /api/v1/reports/summary. */
interface SummaryReport {
  unbilled: { count: number; totalCents: number };
  outstanding: {
    outstandingCents: number;
    overdueCents: number;
    currentCents: number;
  };
  recurringSplit: { recurringCents: number; oneOffCents: number };
  scheduledVsInvoiced: {
    scheduledCents: number;
    invoicedCents: number;
    gapCents: number;
  };
}

/** One month of the 12-month trend. */
interface TrendPoint {
  month: string;
  invoicedCents: number;
  collectedCents: number;
}

/** Response from GET /api/v1/reports/trend. */
interface TrendReport {
  series: TrendPoint[];
}

/** One customer row from GET /api/v1/reports/customers. */
interface CustomerRow {
  customerId: string;
  customerName: string;
  totalCents: number;
}

/** Response from GET /api/v1/reports/customers. */
interface CustomersReport {
  customers: CustomerRow[];
}

/** A selectable reporting period. */
type Period = 'this-month' | 'last-month' | 'this-year';

interface DateRange {
  from: string;
  to: string;
}

/**
 * Compute a YYYY-MM-DD range for the given period, anchored on the
 * organization's "today" (in the org timezone) — never on the browser's clock.
 *
 * The arithmetic is done against the YYYY-MM-DD string, not by constructing
 * Date objects and reading local getters, so it is immune to the runtime's
 * timezone. `todayIn(tz)` already does the timezone-aware formatting.
 */
function rangeFor(period: Period, today: string): DateRange {
  const start = startOfMonth(today);
  if (period === 'this-month') {
    return { from: start, to: today };
  }
  if (period === 'last-month') {
    const lastMonthStart = addMonths(start, -1);
    const lastMonthEnd = addDays(start, -1);
    return { from: lastMonthStart, to: lastMonthEnd };
  }
  // this-year
  const year = today.slice(0, 4);
  return { from: `${year}-01-01`, to: today };
}

/** Human-readable label for a period. */
function periodLabel(period: Period): string {
  if (period === 'this-month') return 'This month';
  if (period === 'last-month') return 'Last month';
  return 'This year';
}

/** The six exportable entities, in the order they appear. */
const EXPORT_ENTITIES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'customers', label: 'Customers' },
  { value: 'properties', label: 'Properties' },
  { value: 'jobs', label: 'Jobs' },
  { value: 'quotes', label: 'Quotes' },
  { value: 'invoices', label: 'Invoices' },
  { value: 'payments', label: 'Payments' },
];

/**
 * A per-section load state. Each of the four new metric groups fetches
 * independently so a failure in one never blanks the others.
 */
interface SectionState<T> {
  data: T | null;
  error: string | null;
}

function initialSection<T>(): SectionState<T> {
  return { data: null, error: null };
}

/**
 * The Reports screen.
 *
 * Shows revenue and completed-job totals for a selected period, plus a CSV
 * export control for each of the six reportable entities. The period is
 * always derived from the organization's today (via todayIn(timezone)) so a
 * UTC runtime never misfiles the last hours of the day.
 *
 * The four new metric groups (unbilled, outstanding, scheduled vs invoiced,
 * recurring vs one-off, the 12-month trend, and top customers) each fetch
 * their own endpoint and render independently: a failure in one shows an
 * inline message for that section only, leaving the rest of the screen intact.
 */
export function AnalyticsPage() {
  const [tz, setTz] = useState<string>('America/Chicago');
  const [period, setPeriod] = useState<Period>('this-month');
  const [revenue, setRevenue] = useState<RevenueReport | null>(null);
  const [jobs, setJobs] = useState<JobsReport | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Per-section state for the four new metric groups.
  const [summary, setSummary] = useState<SectionState<SummaryReport>>(() =>
    initialSection<SummaryReport>()
  );
  const [trend, setTrend] = useState<SectionState<TrendReport>>(() =>
    initialSection<TrendReport>()
  );
  const [customers, setCustomers] = useState<SectionState<CustomersReport>>(() =>
    initialSection<CustomersReport>()
  );

  // Resolve the organization timezone from the session endpoint.
  useEffect(() => {
    void (async () => {
      try {
        const session = await apiGet<OrgSession>('/api/v1/auth/session');
        if (session.organization?.timezone) {
          setTz(session.organization.timezone);
        }
      } catch {
        // Fall back to the default timezone; reports will still load.
      }
    })();
  }, []);

  // The date range is derived from the org's today. `today` is recomputed
  // whenever the timezone resolves, so the range is always correct.
  const today = todayIn(tz);
  const range = rangeFor(period, today);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rev, jb] = await Promise.all([
        apiGet<RevenueReport>(`/api/v1/reports/revenue?from=${range.from}&to=${range.to}`),
        apiGet<JobsReport>(`/api/v1/reports/jobs?from=${range.from}&to=${range.to}`),
      ]);
      setRevenue(rev);
      setJobs(jb);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not load reports.');
      setRevenue(null);
      setJobs(null);
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to]);

  // Fetch the summary (unbilled, outstanding, scheduled vs invoiced,
  // recurring vs one-off) for the selected range. Isolated from the
  // revenue/jobs fetch so a hiccup here does not blank the rest of the screen.
  const fetchSummary = useCallback(async () => {
    setSummary({ data: null, error: null });
    try {
      const data = await apiGet<SummaryReport>(
        `/api/v1/reports/summary?from=${range.from}&to=${range.to}`
      );
      setSummary({ data, error: null });
    } catch (err) {
      setSummary({
        data: null,
        error: err instanceof ApiClientError ? err.message : 'Could not load summary.',
      });
    }
  }, [range.from, range.to]);

  // Fetch the 12-month trend. Isolated so a trend failure never hides the
  // outstanding balance, which is the number the owner came for.
  const fetchTrend = useCallback(async () => {
    setTrend({ data: null, error: null });
    try {
      const data = await apiGet<TrendReport>('/api/v1/reports/trend?months=12');
      setTrend({ data, error: null });
    } catch (err) {
      setTrend({
        data: null,
        error: err instanceof ApiClientError ? err.message : 'Could not load trend.',
      });
    }
  }, []);

  // Fetch the top customers for the selected range. Isolated.
  const fetchCustomers = useCallback(async () => {
    setCustomers({ data: null, error: null });
    try {
      const data = await apiGet<CustomersReport>(
        `/api/v1/reports/customers?from=${range.from}&to=${range.to}`
      );
      setCustomers({ data, error: null });
    } catch (err) {
      setCustomers({
        data: null,
        error: err instanceof ApiClientError ? err.message : 'Could not load customers.',
      });
    }
  }, [range.from, range.to]);

  /**
   * Refetch EVERY section, not just the two the page-level error came from.
   * With failure isolation each section holds its own error, so a Retry that
   * only refetched revenue and jobs cleared the page-level message and left
   * four section errors sitting underneath it — the owner presses Retry, the
   * page still looks broken, and nothing says to press anything else.
   *
   * Declared AFTER the fetchers and depending on them: with an empty dep array
   * it would capture the closures from the first render and refetch the
   * ORIGINAL period after the owner had changed it.
   */
  const retryAll = useCallback(() => {
    void fetchReports();
    void fetchSummary();
    void fetchTrend();
    void fetchCustomers();
  }, [fetchReports, fetchSummary, fetchTrend, fetchCustomers]);

  useEffect(() => {
    void fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    void fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    void fetchTrend();
  }, [fetchTrend]);

  useEffect(() => {
    void fetchCustomers();
  }, [fetchCustomers]);

  if (loading) {
    return (
      <main id="main" className={styles.container}>
        <p className={styles.status} aria-live="polite">
          Loading reports…
        </p>
      </main>
    );
  }

  if (error) {
    return (
      <main id="main" className={styles.container}>
        <p className={styles.status} role="alert">
          {error}
        </p>
        <button type="button" className={styles.retryButton} onClick={retryAll}>
          Retry
        </button>
      </main>
    );
  }

  // A period with no activity returns zeros — a legitimate answer. Render it
  // as $0.00, not as an error or an empty state.
  const netRevenue = revenue?.netRevenueCents ?? 0;
  const tax = revenue?.taxCents ?? 0;
  const invoiced = revenue?.totalInvoicedCents ?? 0;
  const collected = revenue?.totalCollectedCents ?? 0;
  const outstanding = revenue?.outstandingCents ?? 0;
  const completedCount = jobs?.completedCount ?? 0;
  const totalRevenue = jobs?.totalRevenueCents ?? 0;

  return (
    <main id="main" className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>Reports</h1>
        <div className={styles.periodSelector}>
          <label htmlFor="period" className={styles.periodLabel}>
            Period
          </label>
          <select
            id="period"
            className={styles.periodSelect}
            value={period}
            onChange={(e) => setPeriod(e.target.value as Period)}
          >
            <option value="this-month">This month</option>
            <option value="last-month">Last month</option>
            <option value="this-year">This year</option>
          </select>
        </div>
        <p className={styles.rangeText} aria-live="polite">
          {periodLabel(period)}: {range.from} to {range.to}
        </p>
      </header>

      <section className={styles.section} aria-labelledby="revenue-heading">
        <h2 id="revenue-heading" className={styles.sectionTitle}>
          Revenue
        </h2>
        <dl className={styles.stats}>
          {/*
            Revenue first, and it is NET. Sales tax is money collected for
            Louisiana, so putting it in a figure labelled revenue overstates
            what the business earned by exactly the amount it owes.
          */}
          <div className={styles.stat}>
            <dt className={styles.statLabel}>Revenue (excl. tax)</dt>
            <dd className={styles.statValue}>{formatCents(netRevenue)}</dd>
          </div>
          {/*
            Rendered only when there is tax to show. A permanent "$0.00" row
            for a business that does not charge sales tax is noise, and the
            organisation's rate is currently 0.
          */}
          {tax > 0 && (
            <div className={styles.stat}>
              <dt className={styles.statLabel}>Sales tax collected</dt>
              <dd className={styles.statValue}>{formatCents(tax)}</dd>
            </div>
          )}
          <div className={styles.stat}>
            <dt className={styles.statLabel}>Invoiced (incl. tax)</dt>
            <dd className={styles.statValue}>{formatCents(invoiced)}</dd>
          </div>
          <div className={styles.stat}>
            <dt className={styles.statLabel}>Collected</dt>
            <dd className={styles.statValue}>{formatCents(collected)}</dd>
          </div>
          <div className={styles.stat}>
            <dt className={styles.statLabel}>Outstanding</dt>
            <dd className={styles.statValue}>{formatCents(outstanding)}</dd>
          </div>
        </dl>
      </section>

      <section className={styles.section} aria-labelledby="jobs-heading">
        <h2 id="jobs-heading" className={styles.sectionTitle}>
          Completed jobs
        </h2>
        <dl className={styles.stats}>
          <div className={styles.stat}>
            <dt className={styles.statLabel}>Count</dt>
            <dd className={styles.statValue}>{completedCount}</dd>
          </div>
          <div className={styles.stat}>
            <dt className={styles.statLabel}>Total</dt>
            <dd className={styles.statValue}>{formatCents(totalRevenue)}</dd>
          </div>
        </dl>
      </section>

      {/*
        UNBILLED COMPLETED WORK. Finished work never invoiced — money already
        earned, sitting on the table. Given the most visual weight of the new
        metrics. When it is zero, say so positively rather than showing a bare
        $0.00 that reads like a loading failure.
      */}
      <section className={styles.section} aria-labelledby="unbilled-heading">
        <h2 id="unbilled-heading" className={styles.sectionTitle}>
          Unbilled completed work
        </h2>
        {summary.error ? (
          <p className={styles.sectionError} role="alert">
            {summary.error}
          </p>
        ) : summary.data ? (
          <dl className={styles.stats}>
            <div className={styles.stat}>
              <dt className={styles.statLabel}>Count</dt>
              <dd className={styles.statValue}>{summary.data.unbilled.count}</dd>
            </div>
            <div className={styles.stat}>
              <dt className={styles.statLabel}>Total</dt>
              <dd className={styles.statValue}>
                {summary.data.unbilled.count === 0
                  ? 'Everything completed has been invoiced'
                  : formatCents(summary.data.unbilled.totalCents)}
              </dd>
            </div>
          </dl>
        ) : null}
      </section>

      {/*
        OUTSTANDING, split into overdue and current. Overdue is emphasised
        with --color-danger ONLY when it is above zero — a permanently red
        $0.00 trains the owner to ignore the colour.
      */}
      <section className={styles.section} aria-labelledby="outstanding-heading">
        <h2 id="outstanding-heading" className={styles.sectionTitle}>
          Outstanding receivables
        </h2>
        {summary.error ? (
          <p className={styles.sectionError} role="alert">
            {summary.error}
          </p>
        ) : summary.data ? (
          <dl className={styles.stats}>
            <div className={styles.stat}>
              <dt className={styles.statLabel}>Total</dt>
              <dd className={styles.statValue}>
                {formatCents(summary.data.outstanding.outstandingCents)}
              </dd>
            </div>
            {/*
              data-overdue is the testable hook. CSS-module class names are
              rewritten by the bundler, so asserting on `styles.statOverdue`
              from a test means asserting on a value the test cannot name --
              the delivered test tried, and ended up interpolating a regex
              literal into a selector.
            */}
            <div
              data-overdue={summary.data.outstanding.overdueCents > 0}
              className={
                summary.data.outstanding.overdueCents > 0 ? styles.statOverdue : styles.stat
              }
            >
              <dt className={styles.statLabel}>Overdue</dt>
              <dd className={styles.statValue}>
                {formatCents(summary.data.outstanding.overdueCents)}
              </dd>
            </div>
            <div className={styles.stat}>
              <dt className={styles.statLabel}>Current</dt>
              <dd className={styles.statValue}>
                {formatCents(summary.data.outstanding.currentCents)}
              </dd>
            </div>
          </dl>
        ) : null}
      </section>

      {/*
        SCHEDULED VS INVOICED. Show scheduled, invoiced, and the gap. The gap
        is labelled as what it is: work discounted, repriced at the door, or
        never billed. A NEGATIVE gap is legitimate (more was billed than
        scheduled) and renders as negative, not clamped to zero.

        THESE TWO NUMBERS ARE NEVER ADDED TOGETHER. They are the same money
        counted two ways; the server subtracts them and so does the screen.
      */}
      <section className={styles.section} aria-labelledby="scheduled-heading">
        <h2 id="scheduled-heading" className={styles.sectionTitle}>
          Scheduled vs invoiced
        </h2>
        {summary.error ? (
          <p className={styles.sectionError} role="alert">
            {summary.error}
          </p>
        ) : summary.data ? (
          <dl className={styles.stats}>
            <div className={styles.stat}>
              <dt className={styles.statLabel}>Scheduled</dt>
              <dd className={styles.statValue}>
                {formatCents(summary.data.scheduledVsInvoiced.scheduledCents)}
              </dd>
            </div>
            <div className={styles.stat}>
              <dt className={styles.statLabel}>Invoiced</dt>
              <dd className={styles.statValue}>
                {formatCents(summary.data.scheduledVsInvoiced.invoicedCents)}
              </dd>
            </div>
            <div className={styles.stat}>
              <dt className={styles.statLabel}>Gap (discounted, repriced, or never billed)</dt>
              <dd className={styles.statValue}>
                {formatCents(summary.data.scheduledVsInvoiced.gapCents)}
              </dd>
            </div>
          </dl>
        ) : null}
      </section>

      {/*
        RECURRING VS ONE-OFF, labelled SCHEDULED WORK — these are job prices,
        not invoiced amounts. The whole risk is it being read as revenue and
        mentally added to the invoiced figure, so the label is asserted in a
        test.
      */}
      <section className={styles.section} aria-labelledby="recurring-heading">
        <h2 id="recurring-heading" className={styles.sectionTitle}>
          Scheduled work
        </h2>
        {summary.error ? (
          <p className={styles.sectionError} role="alert">
            {summary.error}
          </p>
        ) : summary.data ? (
          <dl className={styles.stats}>
            <div className={styles.stat}>
              <dt className={styles.statLabel}>Recurring</dt>
              <dd className={styles.statValue}>
                {formatCents(summary.data.recurringSplit.recurringCents)}
              </dd>
            </div>
            <div className={styles.stat}>
              <dt className={styles.statLabel}>One-off</dt>
              <dd className={styles.statValue}>
                {formatCents(summary.data.recurringSplit.oneOffCents)}
              </dd>
            </div>
          </dl>
        ) : null}
      </section>

      {/*
        THE 12-MONTH TREND. A bar per month, invoiced and collected. PLAIN CSS
        — divs sized by percentage. NOT a charting library: the app has a
        strict CSP with no external requests and no chart dependency.

        A zero-activity month must still occupy its slot. A missing bar reads
        as "no data"; a zero-height bar reads as "no work that month", which is
        the truth.
      */}
      <section className={styles.section} aria-labelledby="trend-heading">
        <h2 id="trend-heading" className={styles.sectionTitle}>
          12-month trend
        </h2>
        {trend.error ? (
          <p className={styles.sectionError} role="alert">
            {trend.error}
          </p>
        ) : trend.data ? (
          <TrendChart series={trend.data.series} />
        ) : null}
      </section>

      {/*
        TOP CUSTOMERS, a simple ordered list, at most 5, saying so if there
        are more. Rendered in the order the endpoint returned them — already
        sorted by revenue descending server-side — and NOT re-sorted
        client-side.
      */}
      <section className={styles.section} aria-labelledby="customers-heading">
        <h2 id="customers-heading" className={styles.sectionTitle}>
          Top customers
        </h2>
        {customers.error ? (
          <p className={styles.sectionError} role="alert">
            {customers.error}
          </p>
        ) : customers.data ? (
          <CustomerList customers={customers.data.customers} />
        ) : null}
      </section>

      <section className={styles.section} aria-labelledby="export-heading">
        <h2 id="export-heading" className={styles.sectionTitle}>
          Export
        </h2>
        <p className={styles.exportHint}>Download a CSV of any entity for the selected range.</p>
        <ul className={styles.exportList}>
          {EXPORT_ENTITIES.map((entity) => {
            const href = `/api/v1/reports/export/${entity.value}?from=${range.from}&to=${range.to}`;
            return (
              <li key={entity.value} className={styles.exportRow}>
                <span className={styles.exportLabel}>{entity.label}</span>
                <a
                  className={styles.exportLink}
                  href={href}
                  download
                  aria-label={`Download ${entity.label} as CSV`}
                >
                  Download CSV
                </a>
              </li>
            );
          })}
        </ul>
      </section>
    </main>
  );
}

/**
 * The 12-month trend, rendered as plain CSS bars sized by percentage.
 *
 * Each month gets a slot regardless of activity: a zero-height bar reads as
 * "no work that month", which is the truth, whereas a missing bar reads as
 * "no data". Bars use --color-primary for invoiced and --color-text-muted for
 * collected so they stay readable in both light and dark mode.
 */
function TrendChart({ series }: { series: TrendPoint[] }) {
  // The tallest bar across both series sets the scale, so the chart is
  // comparable month-to-month. Guard against an all-zero series (which would
  // divide by zero) by falling back to 1.
  let max = 1;
  for (const point of series) {
    if (point.invoicedCents > max) max = point.invoicedCents;
    if (point.collectedCents > max) max = point.collectedCents;
  }

  return (
    <div className={styles.trendChart} role="img" aria-label="12-month trend">
      {series.map((point) => {
        const invoicedPct = (point.invoicedCents / max) * 100;
        const collectedPct = (point.collectedCents / max) * 100;
        return (
          <div key={point.month} className={styles.trendBar}>
            <div
              className={styles.trendBarInvoiced}
              style={{ height: `${invoicedPct}%` }}
              aria-label={`Invoiced ${formatCents(point.invoicedCents)}`}
            />
            <div
              className={styles.trendBarCollected}
              style={{ height: `${collectedPct}%` }}
              aria-label={`Collected ${formatCents(point.collectedCents)}`}
            />
            <span className={styles.trendMonth}>{point.month}</span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Top customers, rendered as an ordered list in the order the endpoint
 * returned them (already sorted by revenue descending server-side). At most
 * five are shown; if there are more, a note says so.
 */
function CustomerList({ customers }: { customers: CustomerRow[] }) {
  const shown = customers.slice(0, 5);
  const hasMore = customers.length > 5;
  return (
    <div className={styles.customerList}>
      <ol className={styles.customerOrder}>
        {shown.map((customer) => (
          <li key={customer.customerId} className={styles.customerRow}>
            <span className={styles.customerName}>{customer.customerName}</span>
            <span className={styles.customerTotal}>{formatCents(customer.totalCents)}</span>
          </li>
        ))}
      </ol>
      {hasMore && (
        <p className={styles.customerMore} aria-live="polite">
          Showing top 5 of {customers.length}
        </p>
      )}
    </div>
  );
}
